// src/pages/AdminPanel/IssueUploader.jsx
// Upload DebtStudio Issues (CSV) into ds_deals_lib (admin only)

import React, { useState } from "react";
import Papa from "papaparse";
import SectionCard from "components/SectionCard";
import { DropdownSelect, FileBrowseInput } from "components/InputStyled";
import { supabase } from "services/supabaseClient";
import { logAudit } from "lib/audit";
import { useProfile } from "context/ProfileContext";
import {
  toISO,
  toNullableFloat,
  cleanVendorText,
  parseBooleanYN,
  chunk,
} from "lib/uploadHelpers";

/* ─────────────────────────────── Constants ─────────────────────────────── */

const COUPON_FREQUENCIES = new Set([
  "monthly",
  "quarterly",
  "semiannual",
  "annual",
]);

const DEBT_ISSUES_REQUIRED_HEADERS = [
  "issuer_legal_name",
  "issuer_status",
  "name",
  "status",
  "deal_type",
  "tax_status",
  "interest_mode",
  "sharing",
  "lien_priority",
  "dated_date",
  "delivery_date",
  "first_coupon_date",
  "denomination",
  "coupon_rate",
  "day_count",
  "coupon_frequency",
  "principal_frequency",
  "issue_size",
  "fiscal_year_end",
  "structure_date",
  "callable",
  "structure_call_date",
  "structure_principal",
  "structure_coupon",
  "structure_yield",
  "structure_price",
  "structure_bond_type",
  "structure_term_group",
];

const ISSUE_UPSERT_ON_CONFLICT = [
  "issuer_id",
  "name",
  "dated_date",
  "final_maturity_date",
];

const ISSUE_BATCH_SIZE = 250;

/* ─────────────────────────────── Helpers ─────────────────────────────── */

function validateDebtIssueHeaders(parsedHeaders) {
  const headers = parsedHeaders.map((h) => String(h || "").toLowerCase().trim());
  return DEBT_ISSUES_REQUIRED_HEADERS.every((h) => headers.includes(h));
}

const normLower = (v) => String(v ?? "").trim().toLowerCase();

const normKey = (v) => String(v ?? "").trim();

const toNullableISO = (v) => {
  const s = String(v ?? "").trim();
  if (!s || /^#n\/?a\b/i.test(s)) return null;
  return toISO(s);
};

function stableIssueKeyFromRow(r, issuerId) {
  return [
    issuerId,
    normKey(r.issuer_status),
    normKey(r.name),
    normKey(r.status),
    normKey(r.deal_type),
    normKey(r.tax_status),
    normKey(r.interest_mode),
    normKey(r.sharing),
    normKey(r.lien_priority),
    r.dated_date,
    r.delivery_date,
    r.first_coupon_date,
    normKey(r.denomination),
    normKey(r.coupon_rate),
    normKey(r.day_count),
    normKey(r.coupon_frequency),
    normKey(r.principal_frequency),
    normKey(r.fiscal_year_end),
  ].join("||");
}

/**
 * decodeIssueName
 * ------------------------------------------------------------------
 * If input starts with "decode-" (case-insensitive), parse dash-delimited
 * tokens and produce a formatted name per the mapping rules.
 *
 * If not decode- prefixed, return the input unchanged (trimmed).
 *
 * Output assembly:
 *  - Space-separated parts
 *  - Exception: if year + series letter exist, join as "YYYYL" (no space)
 *  - Include "Series" only if year or letter exists
 *  - Disregard any values/tokens starting with "#N/A"
 *
 * Enhancements:
 *  - Broader GO / GO Ltd / Revenue / Revenue Notes detection
 *  - Broader refunding detection
 *  - Supports vendor spelling variations, including "Ctfs Oblg" and "Lein"
 *  - Supports taxable, variable rate, senior lien and junior lien variations
 */
function decodeIssueName(rawName) {
  const original = rawName == null ? "" : String(rawName);
  const trimmed = original.trim();
  if (!trimmed) return trimmed;

  if (/^#n\/?a\b/i.test(trimmed)) return null;

  const lower = trimmed.toLowerCase();
  if (!lower.startsWith("decode-")) return trimmed;

  const remainder = trimmed.slice(trimmed.indexOf("-") + 1);
  const tokensRaw = remainder
    .split("-")
    .map((t) => String(t ?? "").trim())
    .filter((t) => t && !/^#n\/?a\b/i.test(t));

  if (!tokensRaw.length) return trimmed;

  const tokensLower = tokensRaw.map((t) => t.toLowerCase());
  const hasToken = (predicate) => tokensLower.some(predicate);

  let out1 = null;
  let out2 = null;
  let out3 = null;
  let out4a = null;
  let out4b = null;
  let out5 = null;
  let year = null;
  let letter = null;
  let out9 = null;

  if (
    hasToken(
      (t) =>
        t.startsWith("var") ||
        t.includes("variable rate") ||
        t.includes("variable")
    )
  ) {
    out1 = "Variable Rate";
  }

  if (
    hasToken(
      (t) =>
        t.includes("junior lien") ||
        t.includes("jr lien") ||
        t.includes("junior lein") ||
        t.startsWith("junior")
    )
  ) {
    out2 = "Junior Lien";
  } else if (
    hasToken(
      (t) =>
        t.includes("senior lien") ||
        t.includes("sr lien") ||
        t.includes("senior lein") ||
        t.startsWith("senior")
    )
  ) {
    out2 = "Senior Lien";
  }

  if (hasToken((t) => t === "ref" || t.includes("refund"))) {
    out3 = "Ref";
  }

  if (
    hasToken(
      (t) =>
        t.includes("ctfs oblig") ||
        t.includes("ctfs oblg") ||
        t.includes("certificates of obligation") ||
        t.includes("certificate of obligation")
    )
  ) {
    out4a = "Certs Oblig";
  }

  if (hasToken((t) => t.includes("revenue"))) {
    out4b = hasToken((t) => t.includes("note")) ? "Rev Notes" : "Rev";
  } else if (
    hasToken(
      (t) =>
        t.includes("general obligation ltd") ||
        t.includes("general obligation limited") ||
        t.includes("go ltd") ||
        t.includes("g.o. ltd") ||
        t.includes("limited tax")
    )
  ) {
    out4b = "GO Ltd";
  } else if (
    hasToken(
      (t) =>
        t.includes("general obligation unltd") ||
        t.includes("general obligation unlimited") ||
        t.includes("general obligation") ||
        t === "go" ||
        t === "g.o."
    )
  ) {
    out4b = "GO";
  }

  if (hasToken((t) => t === "txbl" || t.includes("taxable"))) {
    out5 = "Taxable";
  }

  for (let i = 0; i < tokensRaw.length; i += 1) {
    const t = tokensRaw[i];
    if (/^\d{4}$/.test(t)) {
      const y = Number(t);
      if (Number.isFinite(y) && y >= 1800 && y <= 2200) {
        year = t;
        break;
      }
    }
  }

  for (let i = 0; i < tokensRaw.length; i += 1) {
    const t = tokensRaw[i];
    const tl = tokensLower[i];

    const m = t.match(/^\s*ser\s+([A-Za-z])\s*$/i);
    if (m && m[1]) {
      letter = String(m[1]).toUpperCase();
      break;
    }

    if (tl === "ser" && i + 1 < tokensRaw.length) {
      const next = tokensRaw[i + 1].trim();
      if (/^#n\/?a\b/i.test(next)) continue;

      if (/^[A-Za-z]$/.test(next)) {
        letter = next.toUpperCase();
        break;
      }
    }
  }

  if (
    hasToken(
      (t) =>
        t === "build america bonds" ||
        t.includes("build america bond")
    )
  ) {
    out9 = "BABs";
  }

  const parts = [];
  if (out1) parts.push(out1);
  if (out2) parts.push(out2);
  if (out3) parts.push(out3);
  if (out4a) parts.push(out4a);
  if (out4b) parts.push(out4b);
  if (out5) parts.push(out5);

  const hasSeriesBlock = !!year || !!letter;
  if (hasSeriesBlock) {
    parts.push("Series");
    if (year && letter) parts.push(`${year}${letter}`);
    else if (year) parts.push(year);
    else if (letter) parts.push(letter);
  }

  if (out9) parts.push(out9);

  const decoded = parts.join(" ").trim();
  return decoded || trimmed;
}

/* ─────────────────────────────── Component ─────────────────────────────── */

export default function IssuerUploader() {
  const { profile, loading: profileLoading } = useProfile();

  const isPlatformAdmin = !!profile?.is_admin;
  const userId = profile?.id ?? null;

  const [mode, setMode] = useState("issue_data");
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState("");
  const [preview, setPreview] = useState([]);

  if (profileLoading) {
    return <div className="p-4 text-muted">Loading…</div>;
  }

  if (!isPlatformAdmin) {
    return <div className="p-4 text-app">Platform admin access required.</div>;
  }

  const handleFileChange = (selectedFile) => {
    setFile(selectedFile);
    setStatus("");
    setPreview([]);
  };

  const parseAndUpload = async () => {
    if (!file) return setStatus("❌ Choose a file first.");
    if (!userId) return setStatus("❌ You must be signed in.");

    if (mode !== "issue_data") {
      setStatus("❌ Unsupported upload type.");
      return;
    }

    setStatus("Parsing…");

    const actionForMode = "debt_issues_upload";

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        delimiter: ",",
        complete: async ({ data, errors, meta }) => {
          try {
            if (errors?.length) {
              setStatus(`❌ CSV parse error: ${errors[0].message}`);
              return;
            }

            if (!data?.length) {
              setStatus("❌ No rows found in CSV.");
              return;
            }

            const parsedHeaders = meta.fields || Object.keys(data[0] || {});
            if (!validateDebtIssueHeaders(parsedHeaders)) {
              setStatus(
                "❌ This file does not look like a valid Issue Data CSV (unexpected headers)."
              );
              return;
            }

            const cleaned = data
              .map((r) => ({
                issuer_legal_name: cleanVendorText(r.issuer_legal_name),
                issuer_status: cleanVendorText(r.issuer_status),

                name: decodeIssueName(r.name),

                status: cleanVendorText(r.status),
                deal_type: cleanVendorText(r.deal_type),
                tax_status: cleanVendorText(r.tax_status),
                interest_mode: cleanVendorText(r.interest_mode),
                sharing: cleanVendorText(r.sharing),
                lien_priority: cleanVendorText(r.lien_priority),

                dated_date: toNullableISO(r.dated_date),
                delivery_date: toNullableISO(r.delivery_date),
                first_coupon_date: toNullableISO(r.first_coupon_date),

                denomination: toNullableFloat(r.denomination),
                coupon_rate: toNullableFloat(r.coupon_rate),
                issue_size: toNullableFloat(r.issue_size),

                day_count: cleanVendorText(r.day_count),
                coupon_frequency: normLower(r.coupon_frequency),
                principal_frequency: normLower(r.principal_frequency),
                fiscal_year_end: cleanVendorText(r.fiscal_year_end),

                structure_date: toNullableISO(r.structure_date),
                callable: parseBooleanYN(r.callable),
                structure_call_date: toNullableISO(r.structure_call_date),
                structure_principal: toNullableFloat(r.structure_principal),
                structure_coupon: toNullableFloat(r.structure_coupon),
                structure_yield: toNullableFloat(r.structure_yield),
                structure_price: toNullableFloat(r.structure_price),
                structure_bond_type: normLower(r.structure_bond_type),
                structure_term_group: cleanVendorText(r.structure_term_group),
              }))
              .filter((r) => r.issuer_legal_name && r.name && r.structure_date);

            if (!cleaned.length) {
              setStatus(
                "❌ No usable rows found (missing issuer_legal_name, name, or structure_date)."
              );
              return;
            }

            const badFreq = cleaned.find(
              (r) =>
                r.coupon_frequency &&
                !COUPON_FREQUENCIES.has(r.coupon_frequency)
            );
            if (badFreq) {
              setStatus(
                `❌ Invalid coupon_frequency "${badFreq.coupon_frequency}". Expected one of: ${Array.from(
                  COUPON_FREQUENCIES
                ).join(", ")}`
              );
              return;
            }

            const badBondType = cleaned.find(
              (r) =>
                r.structure_bond_type &&
                r.structure_bond_type !== "serial" &&
                r.structure_bond_type !== "term"
            );
            if (badBondType) {
              setStatus(
                `❌ Invalid structure_bond_type "${badBondType.structure_bond_type}". Expected serial or term.`
              );
              return;
            }

            const badIssueSize = cleaned.find(
              (r) =>
                r.issue_size != null &&
                (Number.isNaN(r.issue_size) || r.issue_size < 0)
            );
            if (badIssueSize) {
              setStatus(
                `❌ Invalid issue_size "${badIssueSize.issue_size}". Expected a non-negative number when provided.`
              );
              return;
            }

            const issuerNames = Array.from(
              new Set(cleaned.map((r) => r.issuer_legal_name).filter(Boolean))
            );

            setStatus(`Resolving ${issuerNames.length} issuer name(s)…`);

            const { data: issuerRows, error: issuerErr } = await supabase
              .from("issuers")
              .select("id, legal_name")
              .in("legal_name", issuerNames);

            if (issuerErr) throw new Error(`Issuers lookup: ${issuerErr.message}`);

            const issuerMap = new Map();
            for (const ir of issuerRows || []) issuerMap.set(ir.legal_name, ir.id);

            const missingIssuers = issuerNames.filter((n) => !issuerMap.has(n));
            if (missingIssuers.length) {
              setStatus(
                `❌ issuer_legal_name not found in issuers.legal_name:\n` +
                  missingIssuers.slice(0, 25).join("\n") +
                  (missingIssuers.length > 25
                    ? `\n… (${missingIssuers.length - 25} more)`
                    : "")
              );
              return;
            }

            const groups = new Map();
            let callableMismatches = 0;

            for (const r of cleaned) {
              const issuerId = issuerMap.get(r.issuer_legal_name);
              const key = stableIssueKeyFromRow(r, issuerId);

              if (!groups.has(key)) {
                groups.set(key, {
                  issuer_id: issuerId,
                  issuer_legal_name: r.issuer_legal_name,
                  issueMeta: r,
                  structure: [],
                  issueSizes: new Set(),
                });
              }

              if (r.issue_size != null) {
                groups.get(key).issueSizes.add(Number(r.issue_size));
              }

              let callDate = r.structure_call_date;
              if (r.callable === false && callDate) {
                callableMismatches += 1;
                callDate = null;
              }
              if (r.callable === true && !callDate) {
                callableMismatches += 1;
              }

              const principal = r.structure_principal ?? 0;
              if (principal == null || Number.isNaN(principal) || principal < 0) {
                throw new Error(`Invalid structure_principal at ${r.structure_date}`);
              }

              const bondType = r.structure_bond_type || "serial";

              groups.get(key).structure.push({
                date: r.structure_date,
                call_date: callDate || null,
                principal,
                coupon: r.structure_coupon ?? null,
                yield: r.structure_yield ?? null,
                price: r.structure_price ?? null,
                bond_type: bondType,
                term_group: bondType === "term" ? r.structure_term_group ?? null : null,
                interest: 0,
                total: 0,
                outstanding: 0,
              });
            }

            const libRows = [];

            for (const g of groups.values()) {
              const { issuer_id, issuer_legal_name, issueMeta, issueSizes } = g;

              if (!issueSizes.size) {
                throw new Error(
                  `Missing issue_size for issue "${issueMeta.name}" for issuer "${issuer_legal_name}". All rows were NULL.`
                );
              }

              if (issueSizes.size > 1) {
                throw new Error(
                  `Inconsistent issue_size values for issue "${issueMeta.name}" for issuer "${issuer_legal_name}". Found: ${Array.from(
                    issueSizes
                  ).join(", ")}.`
                );
              }

              g.structure.sort((a, b) => String(a.date).localeCompare(String(b.date)));

              const dates = g.structure.map((x) => x.date).filter(Boolean);
              const firstPrincipalDate = dates[0] || null;
              const finalMaturityDate = dates.length ? dates[dates.length - 1] : null;

              const parAmount = Array.from(issueSizes)[0];

              if (!finalMaturityDate) {
                throw new Error(
                  `Could not derive final_maturity_date for issue "${issueMeta.name}"`
                );
              }

              libRows.push({
                created_by: userId,

                issuer_id,
                issuer_name_raw: issuer_legal_name,
                issuer_status: issueMeta.issuer_status || "canonical",

                name: issueMeta.name,
                status: issueMeta.status || "final",

                deal_type: issueMeta.deal_type,
                tax_status: issueMeta.tax_status,
                interest_mode: issueMeta.interest_mode,
                sharing: issueMeta.sharing,
                lien_priority: issueMeta.lien_priority,

                dated_date: issueMeta.dated_date,
                delivery_date: issueMeta.delivery_date,
                first_coupon_date: issueMeta.first_coupon_date,
                first_principal_date: firstPrincipalDate,
                final_maturity_date: finalMaturityDate,

                par_amount: parAmount,

                denomination: issueMeta.denomination,
                coupon_rate: issueMeta.coupon_rate,

                day_count: issueMeta.day_count,
                coupon_frequency: issueMeta.coupon_frequency,
                principal_frequency: issueMeta.principal_frequency,

                fiscal_year_end: issueMeta.fiscal_year_end || null,
                pricing_mode: null,
                scale: null,
                cost_of_issuance: null,
                additional_sources: null,
                refunded_issues: null,
                structure_mode: "level_payment",
                target_type: "par_amount",
                target_value: 0,
                target_gross: 0,
                capi_enabled: false,
                capi_date: null,
                capi_investment_rate: null,
                first_call_date: null,

                structure: g.structure,
              });
            }

            if (!libRows.length) {
              setStatus("❌ No issues were produced from the file.");
              return;
            }

            setPreview(libRows.slice(0, 2));
            setStatus(`Uploading ${libRows.length} issue(s) to ds_deals_lib (upsert)…`);

            for (const batch of chunk(libRows, ISSUE_BATCH_SIZE)) {
              const { error } = await supabase
                .from("ds_deals_lib")
                .upsert(batch, { onConflict: ISSUE_UPSERT_ON_CONFLICT });
              if (error) throw new Error(`ds_deals_lib upsert: ${error.message}`);
            }

            await logAudit({
              category: "upload",
              action: actionForMode,
              success: true,
              detail: {
                fileName: file.name,
                issues: libRows.length,
                rows: cleaned.length,
                issuers: issuerNames,
                callableMismatches,
                uploadType: mode,
              },
            });

            setStatus(
              `✅ Upload complete.\n` +
                `Issues upserted: ${libRows.length}\n` +
                `Maturity rows processed: ${cleaned.length}` +
                (callableMismatches
                  ? `\n⚠️ Callable/call-date mismatches: ${callableMismatches} (some call dates may have been cleared)`
                  : "")
            );
          } catch (e) {
            await logAudit({
              category: "upload",
              action: actionForMode,
              success: false,
              detail: { fileName: file?.name, error: e.message, uploadType: mode },
            });
            setStatus(`❌ Upload error: ${e.message}`);
          }
        },
        error: async (err) => {
          await logAudit({
            category: "upload",
            action: actionForMode,
            success: false,
            detail: { fileName: file?.name, error: err.message, uploadType: mode },
          });
          setStatus(`❌ CSV parse error: ${err.message}`);
        },
      });
    } catch (e) {
      await logAudit({
        category: "upload",
        action: actionForMode,
        success: false,
        detail: { fileName: file?.name, error: e.message, uploadType: mode },
      });
      setStatus(`❌ Upload error: ${e.message}`);
    }
  };

  return (
    <SectionCard title="Upload DebtStudio Issues">
      <div className="flex flex-wrap items-end gap-3">
        <DropdownSelect
          label="Upload Type"
          value={mode}
          onChange={setMode}
          options={[{ value: "issue_data", label: "Issue Data (CSV)" }]}
          placeholder=""
          className="min-w-72"
        />

        <FileBrowseInput
          label="Upload File"
          file={file}
          accept=".csv"
          placeholder="Select a CSV file"
          onFileChange={handleFileChange}
          className="min-w-96"
        />

      </div>

      <button
        onClick={parseAndUpload}
        className="btn btn-primary mt-3 mb-4 disabled:opacity-50"
        disabled={!file}
      >
        Upload
      </button>

      {status && <p className="text-sm whitespace-pre-line mb-3">{status}</p>}

      {preview.length > 0 && (
        <div className="text-xs text-muted">
          <p className="font-semibold">Preview (issue rows):</p>
          <pre className="overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(preview, null, 2)}
          </pre>
        </div>
      )}

      <p className="text-xs text-muted">
        Notes: issuer is resolved by{" "}
        <span className="font-semibold">issuer_legal_name</span> matching{" "}
        <span className="font-semibold">issuers.legal_name</span>.{" "}
        <span className="font-semibold">par_amount</span> is sourced directly
        from the single non-null{" "}
        <span className="font-semibold">issue_size</span> value found across rows
        for that issue. If all rows have NULL{" "}
        <span className="font-semibold">issue_size</span>, or if multiple non-null
        values are found within the same issue, the upload fails with an error.{" "}
        <span className="font-semibold">first_principal_date</span> and{" "}
        <span className="font-semibold">final_maturity_date</span> are derived
        from the min/max structure_date values. sinking_* columns are ignored. For
        name: values starting with{" "}
        <span className="font-semibold">decode-</span> are parsed and normalized
        before insert. Date values starting with{" "}
        <span className="font-semibold">#N/A</span>, including{" "}
        <span className="font-semibold">#N/A N/A</span>, are treated as NULL.
      </p>
    </SectionCard>
  );
}
